/**
 * /alerts - the alerting page.
 *
 * Layout, top to bottom:
 *   the alert itself, one strip across the full width
 *   the dissemination chain - how the message actually reaches a handset
 *   the affected area, the settlement table and the channel matrix, side by side
 *   the operational half: acknowledgement, lifecycle, thresholds, audit log
 *   every message the chain carries, from this run
 *   the dispatch timeline, collapsed - a supporting view, not the centrepiece
 *
 * Everything is computed by /api/alerts from one simulation run. Clicking a
 * settlement on the map or in the table filters the whole page to it; the
 * threshold sliders re-evaluate the same run against the edited rule.
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '../api/client'
import { useAppStore } from '../store/useAppStore'
import { AffectedAreaMap } from './alerts/AffectedAreaMap'
import { AlertStrip } from './alerts/AlertStrip'
import { ArchitectureDiagram } from './alerts/ArchitectureDiagram'
import { ChannelMatrix } from './alerts/ChannelMatrix'
import { DispatchTimeline } from './alerts/DispatchTimeline'
import { MessageTemplates } from './alerts/MessageTemplates'
import { Operations } from './alerts/Operations'
import { SettlementTable } from './alerts/SettlementTable'
import { TimeToImpact } from './alerts/TimeToImpact'
import { HAZARD, HAZARD_LABELS } from '../components/Legend'
import { SEVERITY_ORDER, postJson } from './alerts/shared'
import type { Architecture, Evaluation, Meta, SettlementRow } from './alerts/shared'

const EDITABLE = ['immediate_lead_min', 'expected_lead_min', 'decision_delay_min', 'ack_window_min']

export default function Alerts() {
  const activeSiteId = useAppStore((s) => s.activeSiteId)
  const meta = useQuery<Meta>({
    queryKey: ['alerts-meta', activeSiteId],
    queryFn: () => api.raw(`/alerts/meta?site_id=${encodeURIComponent(activeSiteId)}`),
  })
  const architecture = useQuery<Architecture>({
    queryKey: ['alerts-architecture'],
    queryFn: () => api.raw('/alerts/architecture'),
    staleTime: Infinity,
  })

  const [runChoice, setRunChoice] = useState<string | null>(null)
  const runId = runChoice ?? meta.data?.runs[0]?.id ?? null

  const [ruleValues, setRuleValues] = useState<Record<string, number>>({})
  const rulesKey = JSON.stringify(ruleValues)
  const evaluation = useQuery<Evaluation>({
    queryKey: ['alerts-eval', runId, rulesKey],
    queryFn: () =>
      postJson<Evaluation>(
        `/alerts/runs/${runId}/evaluate`,
        Object.keys(ruleValues).length ? { rules: ruleValues } : {},
      ),
    enabled: Boolean(runId),
    placeholderData: (previous) => previous,
  })

  const [selected, setSelected] = useState<SettlementRow | null>(null)
  const [t, setT] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [timelineOpen, setTimelineOpen] = useState(false)
  const [confirmed, setConfirmed] = useState<Record<string, number>>({})

  const data = evaluation.data
  const alerted = useMemo(
    () => (data?.settlements ?? []).filter((s) => s.alert.alert),
    [data],
  )
  const reached = useMemo(
    () => alerted.filter((s) => s.arrival_time_min !== null),
    [alerted],
  )
  const advisory = useMemo(
    () => alerted.filter((s) => s.arrival_time_min === null),
    [alerted],
  )

  // The selected settlement is a row of THIS evaluation, so it has to be
  // re-resolved when the run or a threshold changes - otherwise the strip keeps
  // showing the figures from the evaluation the click happened on.
  const filter = useMemo(
    () =>
      selected === null
        ? null
        : (alerted.find((s) => s.settlement_id === selected.settlement_id) ?? null),
    [selected, alerted],
  )

  const events = useMemo(
    () => (filter ? (data?.events ?? []).filter((e) => e.settlement_id === filter.settlement_id) : (data?.events ?? [])),
    [data, filter],
  )
  const maxT = useMemo(
    () =>
      Math.max(
        150,
        ...events.map((e) => e.t_notified_min),
        ...reached.map((s) => s.arrival_time_min as number),
      ) * 1.05,
    [events, reached],
  )

  // The page clock opens at the moment the last dispatch has gone out, so the
  // operations panel is answering a real question on arrival - who has not
  // confirmed - rather than showing a column of "not yet sent". The timeline
  // rewinds it.
  const lastDispatch = useMemo(
    () => (data?.events.length ? Math.max(...data.events.map((e) => e.t_notified_min)) : 0),
    [data],
  )
  useEffect(() => {
    if (lastDispatch) setT((current) => (current === 0 ? lastDispatch : current))
  }, [lastDispatch])

  useEffect(() => {
    if (!data) return
    setRuleValues((current) => {
      if (Object.keys(current).length) return current
      const next: Record<string, number> = {}
      for (const rule of data.rules) {
        if (EDITABLE.includes(rule.key)) next[rule.key] = rule.value as number
      }
      return next
    })
  }, [data])

  const worst =
    reached
      .map((s) => s.alert.severity as string)
      .sort((a, b) => SEVERITY_ORDER.indexOf(a) - SEVERITY_ORDER.indexOf(b))[0] ??
    alerted.map((s) => s.alert.severity as string).sort(
      (a, b) => SEVERITY_ORDER.indexOf(a) - SEVERITY_ORDER.indexOf(b),
    )[0]

  const tableRows = filter ? reached.filter((s) => s.settlement_id === filter.settlement_id) : reached
  const messageSettlement = filter ?? reached[0] ?? null

  const heading = (n: number, title: string) => (
    <h2 className="mb-3 mt-8 flex items-baseline gap-3 first:mt-0" style={{ fontSize: 'var(--fs-20)', fontWeight: 600, color: 'var(--ink-text)' }}>
      <span className="font-mono" style={{ fontSize: 'var(--fs-14)', color: 'var(--ink-text-faint)' }}>{n}</span>
      {title}
    </h2>
  )

  return (
    <div
      className="instrument h-full overflow-y-auto"
      style={{ background: 'var(--ink-bg)' }}
      data-testid="alerts-page"
    >
      <div className="mx-auto max-w-[1860px] px-4 pb-8 pt-4">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span className="section-label">Scenario</span>
          {meta.data && meta.data.runs.length > 1 && (
            <select
              className="ctl"
              value={runId ?? ''}
              onChange={(event) => {
                setRunChoice(event.target.value)
                setSelected(null)
                setConfirmed({})
                setT(0)
              }}
              style={{ minWidth: 360 }}
            >
              {meta.data.runs.map((run) => (
                <option key={run.id} value={run.id}>
                  {run.scenario ?? run.id}
                </option>
              ))}
            </select>
          )}
          {data && (
            <span className="font-mono" style={{ fontSize: 'var(--fs-12)', color: 'var(--ink-text-faint)' }}>
              run {data.run.run_id} · {Math.round(data.run.duration_min / 60)} h simulated · {data.run.time_origin}
            </span>
          )}
        </div>

        {data && runId ? (
          <>
            {/* 1. Alerts by location - what a district officer reads first. */}
            <section data-testid="alerts-by-location">
              {heading(1, 'Alerts by location')}
              {data.location_kind === 'crossing' && (
                <div className="mb-4 grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]" data-testid="reach-context">
                  <div className="panel">
                    {data.no_populated_place && (
                      <p className="text-16 font-semibold" data-testid="no-populated-place">
                        No populated place lies within the modelled extent.
                      </p>
                    )}
                    {data.populated_places && (
                      <p className="mt-1 text-12" style={{ color: 'var(--ink-text-faint)' }}>
                        Checked: {data.populated_places.osm_places_checked} {data.populated_places.source}. The alert locations below
                        are the crossings and roads the modelled flood reaches; the alert area is not extended beyond the modelled extent.
                      </p>
                    )}
                    {(data.districts ?? []).length > 0 && (
                      <div className="mt-3">
                        <div className="section-label">Recipient</div>
                        <div className="text-14">
                          {data.directory.find((d) => d.role === 'district administration')?.name ?? `District administration, ${data.districts![0]}`}
                        </div>
                        <div className="text-12" style={{ color: 'var(--ink-text-faint)' }}>
                          District: {data.districts!.join(', ')} · {data.district_source}
                        </div>
                      </div>
                    )}
                  </div>
                  {data.reach_hazard_km2 && (
                    <div className="panel" data-testid="reach-hazard">
                      <div className="section-label mb-2">Hazard classification of the reach</div>
                      <table className="w-full text-14 tabular-nums">
                        <tbody>
                          {[4, 3, 2, 1].map((c) => (
                            <tr key={c}>
                              <td className="py-0.5">
                                <span className="mr-2 inline-block h-2 w-2" style={{ background: HAZARD[c] }} />
                                {c} · {HAZARD_LABELS[c]}
                              </td>
                              <td className="text-right font-mono">{(data.reach_hazard_km2![String(c)] ?? 0).toFixed(2)} km²</td>
                            </tr>
                          ))}
                          {(data.reach_excluded_km2 ?? 0) > 0 && (
                            <tr style={{ color: 'var(--ink-text-dim)' }}>
                              <td className="py-0.5">Data-quality cells, not classified</td>
                              <td className="text-right font-mono">{data.reach_excluded_km2!.toFixed(2)} km²</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              <SettlementTable
                rows={tableRows}
                advisory={advisory}
                events={data.events}
                selected={filter}
                onSelect={setSelected}
              />
              {worst && (
                <div className="mt-4 overflow-hidden rounded-[6px]" style={{ border: '1px solid var(--ink-border)' }}>
                  <AlertStrip evaluation={data} alerted={alerted} worst={worst} filter={filter} />
                </div>
              )}
              <div className="mt-4 grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <TimeToImpact
                  rows={filter ? reached.filter((s) => s.settlement_id === filter.settlement_id) : reached}
                  t={t}
                  playing={playing}
                  onPlayToggle={() => {
                    if (t >= maxT) setT(0)
                    setPlaying(!playing)
                  }}
                />
                <AffectedAreaMap runId={runId} settlements={alerted} selected={filter} onSelect={setSelected} />
              </div>
            </section>

            {/* 2. Escalation tiers and the dissemination channels. */}
            <section data-testid="tiers-and-channels">
              {heading(2, 'Escalation tiers and channels')}
              <ChannelMatrix channels={meta.data?.channels ?? {}} tiers={meta.data?.tiers ?? {}} events={events} />
              <div className="mt-4">
                <Operations
                  evaluation={data}
                  ruleValues={ruleValues}
                  onRuleChange={(key, value) => setRuleValues((current) => ({ ...current, [key]: value }))}
                  t={t}
                  confirmed={confirmed}
                  siteId={meta.data?.site.id ?? null}
                  onConfirm={(recipient, at) => setConfirmed((current) => ({ ...current, [recipient]: at }))}
                />
              </div>
              <div className="mt-4">
                <DispatchTimeline
                  open={timelineOpen}
                  onToggle={() => setTimelineOpen((open) => !open)}
                  events={events}
                  reached={reached}
                  t={t}
                  setT={setT}
                  maxT={maxT}
                  playing={playing}
                  setPlaying={setPlaying}
                  populationReached={data.population_reached}
                />
              </div>
            </section>

            {/* 3. The CAP v1.2 document the chain carries. */}
            {messageSettlement && (
              <section data-testid="cap-sample">
                {heading(3, 'CAP v1.2 message')}
                <MessageTemplates
                  runId={runId}
                  settlement={messageSettlement}
                  rules={Object.keys(ruleValues).length ? ruleValues : null}
                />
              </section>
            )}
          </>
        ) : (
          (meta.isPending || evaluation.isFetching) && (
            <div className="grid items-start gap-4">
              <div className="skeleton" style={{ height: 240 }} />
              <div className="skeleton" style={{ height: 240 }} />
            </div>
          )
        )}

        {/* 4. How the message reaches a handset - last. */}
        {architecture.data && (
          <section data-testid="architecture">
            {heading(messageSettlement && data ? 4 : 1, 'Dissemination architecture')}
            <ArchitectureDiagram data={architecture.data} />
          </section>
        )}
      </div>
    </div>
  )
}
